// components/media/MobileFolderToolbar.tsx
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
    ChevronRight,
    FolderPlus,
    Home,
    MoreVertical,
    Trash2,
    UploadCloud
} from "lucide-react";

type MobileFolderToolbarProps = {
    currentPath?: string;
    onNavigate: (path: string) => void;
    onOpenCreateFolder: () => void;
    onOpenUpload: () => void;
    onOpenDelete: () => void;
    disableDelete?: boolean;
};

export function MobileFolderToolbar({
    currentPath,
    onNavigate,
    onOpenCreateFolder,
    onOpenUpload,
    onOpenDelete,
    disableDelete,
}: MobileFolderToolbarProps) {
    const parts = currentPath ? currentPath.split("/").filter(Boolean) : [];
    // Build cumulative paths for each crumb: ["", "a", "a/b", ...]
    const crumbs = parts.map((_, i) => parts.slice(0, i + 1).join("/"));

    return (
        <div className="flex flex-col border-b border-slate-200 bg-white md:hidden">
            {/* Row 1: breadcrumb path */}
            <div className="flex items-center gap-0.5 overflow-x-auto px-2 pt-2 pb-1 scrollbar-none">
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => onNavigate("")}
                    aria-label="Go to root"
                >
                    <Home className="h-3.5 w-3.5" />
                </Button>
                {parts.map((part, i) => (
                    <div key={i} className="flex items-center gap-0.5 shrink-0">
                        <ChevronRight className="h-3.5 w-3.5 text-slate-400" />
                        <button
                            onClick={() => onNavigate(crumbs[i])}
                            className={cn(
                                "px-1 py-0.5 rounded text-xs transition-colors whitespace-nowrap",
                                i === parts.length - 1
                                    ? "font-semibold text-slate-900"
                                    : "text-slate-500 hover:text-slate-900"
                            )}
                        >
                            {part}
                        </button>
                    </div>
                ))}
            </div>

            {/* Row 2: actions */}
            <div className="flex items-center justify-end gap-1 px-2 pb-2">
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs"
                    onClick={onOpenUpload}
                >
                    <UploadCloud className="h-3.5 w-3.5" />
                    Upload
                </Button>

                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={onOpenCreateFolder}>
                            <FolderPlus className="mr-2 h-4 w-4" />
                            New Folder
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            disabled={disableDelete}
                            onClick={onOpenDelete}
                            className="text-destructive focus:text-destructive"
                        >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete Folder
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}
